import { useCallback } from "react";
import {
    UserWorkspaceRole,
    VIEW_ONLY_WRITE_REFUSAL_MESSAGE,
} from "@common/userWorkspaceRoles";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VIEW_ONLY_TOAST_KEY } from "@lib/viewOnlyRefusal";
import { useActiveWorkspaceRole } from "./useActiveWorkspaceRole";

/**
 * Refuses an action for a View-only user by explaining it, rather than by
 * offering a control that cannot work. Product's call: a disabled button with no
 * reason reads as a bug, so the control stays live and the click answers.
 *
 * Compared against `VIEW_ONLY` rather than a positive "can modify" predicate on
 * purpose. The active role reads `null` while the workspace is still loading and
 * for a user with no direct assignment, so a positive predicate would refuse
 * admins mid-load. Here an unresolved role falls through to the existing
 * behaviour and the API stays the real boundary.
 *
 * Use `refuseIfViewOnly` where the click leads somewhere the server cannot
 * refuse in time — an overlay, or an external redirect that leaves the app.
 * Where the next step is a normal request, let it 403: `backendClient` already
 * renders this same message in the same red toast.
 *
 * Use `isViewOnly` on its own to skip an optimistic write (PRO-11356). The 403
 * arrives fine there; the problem is that the cache was already written, so the
 * user sees the new value flash and revert. Skipping the write leaves the
 * server's toast as the single refusal, which is why those paths must not also
 * call `refuseIfViewOnly` — that would say it twice.
 */
export function useViewOnlyRefusal() {
    const toaster = useToaster();
    const isViewOnly = useActiveWorkspaceRole() === UserWorkspaceRole.VIEW_ONLY;

    /** Toasts and returns `true` when the caller must stop. */
    const refuseIfViewOnly = useCallback(() => {
        if (!isViewOnly) {
            return false;
        }

        toaster.error(VIEW_ONLY_WRITE_REFUSAL_MESSAGE, VIEW_ONLY_TOAST_KEY);
        return true;
    }, [isViewOnly, toaster]);

    return { isViewOnly, refuseIfViewOnly };
}
